import { Dayjs } from 'dayjs';

/**
 * Compute the ISO Monday for any date (independent of locale week start)
 * ISO weeks always start on Monday regardless of locale settings
 */
export function getIsoMonday(date: Dayjs): Dayjs {
    return date.subtract((date.day() + 6) % 7, 'day');
}

/**
 * Get ISO week numbers for calendar rows that should be displayed for a given month.
 * This matches how MUI DateCalendar renders calendar weeks (5-6 rows typically).
 *
 * Uses ISO 8601 week numbering:
 * - Week 1 is the first week with at least 4 days in the new year
 * - Weeks start on Monday
 * - Week numbers can be 1-53
 *
 * @param currentDate - Any date within the target month
 * @returns Array of ISO week numbers for the calendar rows
 */
export function getWeekNumbersForMonth(currentDate: Dayjs): number[] {
    const startOfMonth = currentDate.startOf('month');
    const endOfMonth = currentDate.endOf('month');

    // First calendar row's Monday (ISO Monday for the week containing the 1st)
    const rowMonday = getIsoMonday(startOfMonth);

    const weekNumbers: number[] = [];
    for (let i = 0; i < 6; i++) {
        const weekStart = rowMonday.add(i, 'week');
        const weekEnd = weekStart.add(6, 'day');

        // Include rows that overlap the month (inclusive):
        // [weekStart, weekEnd] overlaps [startOfMonth, endOfMonth]
        const overlapsMonth = !(
            weekEnd.isBefore(startOfMonth, 'day') || weekStart.isAfter(endOfMonth, 'day')
        );

        if (!overlapsMonth) break;

        // ISO week number is that of Thursday (ISO standard)
        // Use startOf('day') to normalize time to avoid DST transition issues
        const isoThursday = weekStart.add(3, 'day').startOf('day');
        weekNumbers.push(isoThursday.isoWeek());
    }

    return weekNumbers;
}
